import React, { FC, useCallback } from 'react';
import { FormNodeNameType } from '../../../../shared/kiosk';
import { MaterialIcon } from '../../../components';
import { useTranslation } from '../../../translations';
import { classNames } from '../../../utils';
import { EditorNodeRenderer, RootFormState, SetRoot, ToolboxMode } from './FormElements';
import { useExpressEditorConfig } from './OptionContext';
import { PagesToolbox } from './toolbox/Pages';

interface ExpressEditorToolboxProps {
  readonly nextDragId: number;
  readonly toolboxTypes: FormNodeNameType[];
  readonly state: RootFormState;
  readonly setState: SetRoot;
}

export const ExpressEditorToolbox: FC<ExpressEditorToolboxProps> = ({
  nextDragId,
  toolboxTypes,
  state,
  setState,
}) => {
  const mode = state.toolboxTab;

  const config = useExpressEditorConfig();
  const { ts } = useTranslation();

  const setMode = useCallback(
    (toolboxTab: ToolboxMode) => {
      setState((oldState) => ({ ...oldState, toolboxTab }));
    },
    [setState],
  );

  const toggleClosedMode = () => {
    if (mode === 'closed') {
      setMode('toolbox');
    } else {
      setMode('closed');
    }
  };

  return (
    <nav
      className={classNames(
        {
          'express-editor-closed': mode === 'closed',
        },
        'express-editor-toolbox',
      )}
    >
      <div className="toolbox-header">
        <div className="toolbox-header-collapse" onClick={toggleClosedMode}>
          <MaterialIcon name={mode === 'closed' ? 'menu_open' : 'menu_close'} />
        </div>
        <div
          className={classNames(
            {
              active: mode === 'toolbox',
            },
            'toolbox-header-tab',
          )}
          onClick={() => {
            setMode('toolbox');
          }}
        >
          <div className="icon">
            <MaterialIcon name="construction" />
          </div>
          <div>{ts('toolbox')}</div>
        </div>
        {config.multiplePages && (
          <div
            className={classNames(
              {
                active: mode === 'pages',
              },
              'toolbox-header-tab',
            )}
            onClick={() => {
              setMode('pages');
            }}
          >
            <div className="icon">
              <MaterialIcon name="layers" />
            </div>
            <div>{ts('pages')}</div>
          </div>
        )}
      </div>
      <div className="toolbox-content">
        {mode === 'toolbox' && <ToolboxTab nextDragId={nextDragId} types={toolboxTypes} />}
        {mode === 'pages' && <PagesToolbox setState={setState} state={state} />}
      </div>
    </nav>
  );
};

interface ToolboxTabProps {
  readonly nextDragId: number;
  readonly types: FormNodeNameType[];
}

const ToolboxTab: FC<ToolboxTabProps> = ({ nextDragId, types }) => {
  const elements = types.map((type) => (
    <EditorNodeRenderer key={type} nextId={nextDragId} type={type} />
  ));

  return <div className="editor-toolbox">{elements}</div>;
};
